// RatingModal.js - Star rating component
// Shows after a job is completed so customer can rate their caretaker
// Rating is saved to Firestore and updates provider's average rating

import React, { useState } from 'react';
import { db } from '../firebase';
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  getDoc,
  increment
} from 'firebase/firestore';

function RatingModal({ booking, onClose, onRatingSubmitted }) {
  // State for selected star rating and optional comment
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Submits the rating to Firestore
  const handleSubmitRating = async () => {
    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Step 1 — Save the rating to a ratings collection
      // Each rating is its own document linked to the booking and provider
      await addDoc(collection(db, 'ratings'), {
        bookingId: booking.id,
        providerId: booking.providerId,
        providerName: booking.providerName,
        customerId: booking.customerId,
        rating: rating,
        comment: comment.trim(),
        createdAt: new Date(),
      });

      // Step 2 — Update the booking to mark it as rated
      // This hides the rating prompt after submission
      await updateDoc(doc(db, 'bookings', booking.id), {
        rated: true,
        customerRating: rating,
      });

      // Step 3 — Update the provider's rating stats
      // We fetch their current stats and recalculate the average
      const providerDoc = await getDoc(doc(db, 'users', booking.providerId));
      if (providerDoc.exists()) {
        const providerData = providerDoc.data();
        const currentTotal = providerData.ratingTotal || 0;
        const currentCount = providerData.ratingCount || 0;

        const newTotal = currentTotal + rating;
        const newCount = currentCount + 1;
        const newAverage = (newTotal / newCount).toFixed(1);

        // Update provider's rating fields in Firestore
        // increment() adds to the existing value atomically
        await updateDoc(doc(db, 'users', booking.providerId), {
          ratingTotal: increment(rating),
          ratingCount: increment(1),
          rating: parseFloat(newAverage),
        });
      }

      // Tell parent component the rating was submitted
      onRatingSubmitted();
      onClose();

    } catch (err) {
      console.error('Error submitting rating:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    // Modal overlay — clicking outside closes the modal
    <div className="modal-overlay" onClick={onClose}>

      {/* Modal card — stop click from bubbling to overlay */}
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>

        {/* Close button */}
        <button className="modal-close" onClick={onClose}>✕</button>

        {/* Header */}
        <div className="rating-header">
          <div className="rating-header-icon">⭐</div>
          <h2>Rate Your Caretaker</h2>
          <p>
            How was your experience with{' '}
            <strong>{booking.providerName}</strong>?
          </p>
          <p className="rating-booking-ref">
            Visit for {booking.lovedOneName}
          </p>
        </div>

        {/* ========== STAR RATING ==========
            5 stars that highlight on hover and stay highlighted on click
            hoveredRating tracks which star the mouse is over
            rating tracks which star was clicked */}
        <div className="star-rating">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              className={`star-btn ${star <= (hoveredRating || rating) ? 'star-active' : 'star-inactive'}`}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
            >
              ★
            </button>
          ))}
        </div>

        {/* Star label text — changes based on selected rating */}
        {rating > 0 && (
          <p className="rating-label">
            {rating === 1 && 'Poor'}
            {rating === 2 && 'Fair'}
            {rating === 3 && 'Good'}
            {rating === 4 && 'Very Good'}
            {rating === 5 && 'Excellent'}
          </p>
        )}

        {/* Optional comment */}
        <div className="auth-field">
          <label>
            Leave a comment
            <span className="optional"> (optional)</span>
          </label>
          <textarea
            className="booking-textarea"
            placeholder="Share your experience with this caretaker..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Error message */}
        {error && <div className="auth-error">{error}</div>}

        {/* Submit button */}
        <button
          className="btn-primary btn-full"
          onClick={handleSubmitRating}
          disabled={loading || rating === 0}
        >
          {loading ? 'Submitting...' : 'Submit Rating'}
        </button>

        {/* Skip link */}
        <p className="auth-switch">
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
            onClick={onClose}
          >
            Skip for now
          </button>
        </p>

      </div>
    </div>
  );
}

export default RatingModal;